<!-- @format -->
<template>
    <div class="mdf-activities-widget">
        <div class="widget-header">
            <div class="title-container">
                <h3 v-if="widget.isTimeline">Timeline</h3>
                <h3 v-else>{{ widgetTitle }}</h3>
            </div>
            <div class="svg-container widget-close-button" @click="closeWidget">
                <widget-close-svg />
            </div>
        </div>
        <div class="widget-content widget-content-container">
            <loading-screen-svg v-if="isLoading" />
            <div v-else class="form-row">
                <mdf-activities-container
                    :activities="activities"
                    :client-id="clientId"
                    :agency-id="agencyId"
                    :is-timeline="widget.isTimeline"
                    :is-notification="false"
                >
                </mdf-activities-container>
                <board-no-records-card
                    v-if="showEmptyState"
                    title="No Activities Found"
                    content="Please add a record to populate this widget"
                >
                </board-no-records-card>
            </div>
        </div>
        <div class="widget-footer">
            <span />
            <div class="action-container">
                <app-button @click="closeWidget"> Back </app-button>
            </div>
        </div>
    </div>
</template>

<script>
const AppButton = () => import('@/components/common/app-button');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg');
const MdfActivitiesContainer = () =>
    import('@/components/sliding-widgets/mdf/activities/mdf-activities-container.vue');
const LoadingScreenSvg = () => import('@/components/svg/loading-screen-svg');
const BoardNoRecordsCard = () => import('@/components/board/board-no-records-card');

import { MDFTimelineService } from '@/scripts/services/mdf/mdf-timeline-service';
import EventBus from '@/scripts/event-bus.js';

export default {
    name: 'mdf-activities-widget',

    components: {
        'app-button': AppButton,
        'board-no-records-card': BoardNoRecordsCard,
        'widget-close-svg': WidgetCloseSvg,
        'mdf-activities-container': MdfActivitiesContainer,
        'loading-screen-svg': LoadingScreenSvg,
    },

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            isInitializing: true,
            isLoading: false,
            step: null,
            clientId: this.widget.clientId,
            agencyId: this.widget.agencyId,
            clientAccountId: this.widget.clientAccountId,
            agencyAccountId: this.widget.agencyAccountId,
            requestId: this.widget.requestId,
            isPreapprovedFund: this.widget.isPreapprovedFund,
            showEmptyState: false,

            activities: [],
        };
    },

    computed: {
        widgetTitle() {
            if (this.widget.companyName) {
                return `${this.widget.companyName}'s MDF Activities`;
            }
            return 'MDF Activities';
        },
    },

    created() {
        this.step = 'edit';
        this.isLoading = true;
        this.getActivities()
            .catch((error) => {
                this.$helpers.showFailNotification(
                    'An error occurred while fetching your activities.',
                    { error },
                );
            })
            .finally(() => {
                this.isInitializing = false;
                this.isLoading = false;
            });
    },

    methods: {
        getActivities() {
            if (!this.widget.isTimeline) {
                const observer = this.clientId ? 'clientGetAllHistory' : 'agencyGetAllHistory';
                return new Promise((resolve, reject) => {
                    MDFTimelineService[observer](
                        this.clientAccountId,
                        this.agencyAccountId,
                        this.isPreapprovedFund,
                    )
                        .then(({ data: { data } }) => {
                            const mdfRequestsList = Object.values(data).reverse();
                            if (mdfRequestsList.length === 0) {
                                this.showEmptyState = true;
                            }
                            this.activities = mdfRequestsList.map((funtRequest) => {
                                return {
                                    mdfCategory: funtRequest[0].Body.FundTitle,
                                    activities: this.mapItems(funtRequest),
                                };
                            });
                            resolve();
                        })
                        .catch(() => {
                            reject();
                        });
                });
            } else {
                const observer = this.clientId ? 'clientGet' : 'agencyGet';

                return new Promise((resolve, reject) => {
                    MDFTimelineService[observer](this.clientAccountId, this.requestId)
                        .then(({ data: { data } }) => {
                            this.activities = [
                                { mdfCategory: '', activities: this.mapItems(data.reverse()) },
                            ];
                            resolve();
                        })
                        .catch(() => {
                            reject();
                        });
                });
            }
        },

        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        getActivityTypedBString(dBString) {
            switch (dBString) {
                case 'Vendor_Request_Submitted':
                    return 'mdf-request-recieved-agency';
                case 'Vendor_Request_Deleted':
                    return 'mdf-request-deleted-agency';
                case 'Vendor_Request_Allocated':
                    return 'mdf-allocated-agency';
                case 'Vendor_Request_Pending':
                    return 'mdf-pending-request-agency';
                case 'Vendor_Request_Funds_Approved':
                    return 'mdf-funds-approved-agency';
                case 'Vendor_Request_Denied':
                    return 'mdf-request-denied-agency';
                case 'Vendor_Request_Paid':
                    return 'mdf-funds-paid-agency';
                case 'Vendor_POP_Submitted':
                    return 'mdf-pop-submitted-agency';
                case 'Vendor_POP_Denied':
                    return 'mdf-pop-denied-agency';
                case 'Vendor_Opportunity_Won':
                    return 'mdf-opportunity-won-agency';
                case 'Vendor_Opportunity_Created':
                    return 'mdf-opportunity-created-agency';
                case 'Vendor_Pending_Payment':
                    return 'mdf-invoice-pending-payment-agency';
                case 'Vendor_Proof_Request':
                    return 'mdf-invoice-proof-requested-agency';
                case 'Vendor_Invoice_Marked_As_Paid':
                    return 'mdf-invoice-marked-as-paid-agency';
                case 'Vendor_Invoice_Paid':
                    return 'mdf-invoice-paid-agency';
                case 'Vendor_Banking_Information_Request':
                    return 'mdf-bank-info-request-agency';
                case 'Vendor_Banking_Information_Request_Complete':
                    return 'mdf-bank-info-request-complete-agency';
                case 'Vendor_Funds_Preapproved':
                    return 'mdf-funds-preapproved-agency';
                case 'Partner_Request_Submitted':
                    return 'mdf-request-submitted-partner';
                case 'Partner_MDF_Allocated':
                    return 'mdf-allocated-partner';
                case 'Partner_MDF_Denied':
                    return 'mdf-request-denied-partner';
                case 'Partner_MDF_Deleted':
                    return 'mdf-request-deleted-partner';
                case 'Partner_MDF_Pending':
                    return 'mdf-pending-request-partner';
                case 'Partner_MDF_Funds_Paid':
                    return 'mdf-funds-paid-partner';
                case 'Partner_MDF_Funds_Approved':
                    return 'mdf-funds-approved-partner';
                case 'Partner_POP_Submitted':
                    return 'mdf-pop-submitted-partner';
                case 'Partner_POP_Denied':
                    return 'mdf-pop-denied-partner';
                case 'Partner_POP_Reminder':
                    return 'mdf-pop-deadline-partner';
                case 'Partner_Proof_Of_Payment_Added':
                    return 'mdf-pop-added-partner';
                case 'Partner_Banking_Information_Request':
                    return 'mdf-bank-info-request-partner';
                case 'Partner_Funds_Preapproved':
                    return 'mdf-funds-preapproved-partner';
                case 'POP_Deadline_Reminder':
                    return 'mdf-pop-deadline-partner';
                default:
                    return '';
            }
        },

        getCurrencyAmount(item) {
            if (this.clientId) {
                return `${item.FundRequest.CurrencySymbol}${item.Body.MDFRequestedSelectedCurrency}`;
            } else {
                return `$${item.Body.MDFRequestedUSD}`;
            }
        },

        getAllocatedCurrencyAmount(item) {
            if (this.clientId) {
                return `${item.FundRequest.CurrencySymbol}${
                    item.Body.MDFAllocatedSelectedCurrency ||
                    item.Body.FundsApprovedSelectedCurrency
                }`;
            } else {
                return `$${item.Body.MDFAllocatedUSD || item.Body.FundsApprovedUSD}`;
            }
        },

        getFundsPaidCurrencyAmount(item) {
            if (this.clientId) {
                return `${item.FundRequest.CurrencySymbol}${item.Body.FundsPaidSelectedCurrency}`;
            } else {
                return `$${item.Body.MDFRequestedUSD}`;
            }
        },

        mapItems(items) {
            return items.map((item) => ({
                activityType: this.getActivityTypedBString(item.Type),
                data: {
                    fundTitle: item.Body.FundTitle,
                    fundRequested: this.getCurrencyAmount(item),
                    fundAllocated: this.getAllocatedCurrencyAmount(item),
                    fundsPaid: this.getFundsPaidCurrencyAmount(item),
                    amountPaid: item.Body.AmountPaid,
                    revenue: item.Body.TotalDealValue,
                    actor: {
                        companyProfile: item.Body.PartnerLogo,
                        companyProfileName: item.Body.PartnerName,
                        agencyProfile: item.Body.AgencyLogo,
                        agencyProfileName: item.Body.AgencyName,
                        reason: item.Body.DeniedReason,
                        actorProfileImageUrl: item.Body.ActingUserProfilePicture,
                        actorName: `${item.Body.ActingUserFirstName} ${item.Body.ActingUserLastName}`,
                        contactName: `${item.Body.ContactName} ${item.Body.ContactLastName}`,
                        contactProfileImageUrl: item.Body.ContactProfileImageUrl,
                    },
                },
                deadlineDate: item.CreatedAt,
                Read: true,
                Id: item.Id,
                CreatedAt: item.CreatedAt,
                UpdatedAt: item.UpdatedAt,
            }));
        },
    },
};
</script>

<style>
.mdf-activities-widget .form-row {
    margin-bottom: 20px;
}

.mdf-activities-widget .action-container {
    display: flex;
}

.mdf-activities-widget .action-container .btn {
    margin-left: 15px;
}

.mdf-activities-widget .widget-content {
    width: 100%;
    height: calc(100vh - 152px);
    overflow: auto;
}

.mdf-activities-widget .widget-content-container {
    padding: 8px 0 70px 0;
}

.mdf-activities-widget .widget-content.widget-content-container::-webkit-scrollbar {
    width: 9px;
    height: 9px;
    box-shadow: none;
}

.mdf-activities-widget .widget-content.widget-content-container::-webkit-scrollbar-track {
    border-radius: 32px;
    box-shadow: none;
}

.mdf-activities-widget .widget-content.widget-content-container::-webkit-scrollbar-thumb {
    border-radius: 32px;
    background: #e9e9ef;
    box-shadow: none;
}

.mdf-activities-widget .action-container .btn.white-btn {
    background: #ffffff;
    color: #877d88;
    border: 2px solid #e9e9ef;
}

.mdf-activities-widget .loading-screen {
    text-align: center;
}
</style>
